import type { ReactElement } from 'react'
import type { DeviceType } from '~/types/device-type'

import { TagBadge } from '../Badges'

import AppStoreLogo from '~/assets/app-store-logo.png'
import GooglePlayStoreLogo from '~/assets/google-play-store-logo.png'
import LandscapeSVG from '~/assets/landscape.svg'
import PortraitSVG from '~/assets/portrait.svg'
import TemplateDefaultPreviewLandscape from '~/assets/template-default-preview-landscape.svg'
import TemplateDefaultPreviewPortrait from '~/assets/template-default-preview-portrait.svg'
import Badge from '~/components/Badge'
import Tooltip from '~/components/Tooltip'

import './styles.scss'

const MAX_TAGS_ITEM = 2

interface Props {
  capabilities?: string[]
  device: DeviceType
  onClick?: (key: string) => void
  orientation: 'both' | 'landscape' | 'portrait'
  preview?: string
  targets?: string[]
  templateKey?: string
  title: string
  version: string
}

const TemplateCard = ({
  capabilities,
  device,
  onClick,
  orientation,
  preview,
  targets,
  templateKey,
  title,
  version,
}: Props): ReactElement => {
  const handleCardClick = () => onClick && onClick(templateKey || '')

  return (
    <div className="template-card-component" onClick={handleCardClick}>
      <div className="template-card-component__preview">
        <img
          src={
            preview || (orientation === 'landscape' ? TemplateDefaultPreviewLandscape : TemplateDefaultPreviewPortrait)
          }
        />
        <div className="template-card-component__preview__orientation">
          {(orientation === 'landscape' || orientation === 'both') && <img src={LandscapeSVG} />}
          {(orientation === 'portrait' || orientation === 'both') && <img src={PortraitSVG} />}
        </div>
        <div className="template-card-component__preview__devices">
          {device === 'android' ? (
            <img alt="google-store-logo" key={device} src={GooglePlayStoreLogo} />
          ) : device === 'ios' ? (
            <img alt="app-store-logo" key={device} src={AppStoreLogo} />
          ) : null}
        </div>
      </div>
      <div className="template-card-component__information">
        <div className="template-card-component__information__name">{title}</div>
        {capabilities && (
          <div className="template-card-component__information__capabilities">
            <span className="template-card-component__information__title">Capabilities</span>
            {capabilities.slice(0, MAX_TAGS_ITEM).map((capability, i) => (
              <TagBadge className="capability-badge" key={`${capability}-${i}`} value={capability} />
            ))}
            {capabilities.length > MAX_TAGS_ITEM ? (
              <Tooltip
                align={{ points: ['br', 'tr'] }}
                overlay={
                  <div className="template-tooltips-content">
                    {capabilities.slice(MAX_TAGS_ITEM).map((capability, i) => (
                      <TagBadge className="capability-badge badge" key={`${capability}-${i}`} value={capability} />
                    ))}
                  </div>
                }
                placement="top"
                trigger="hover"
              >
                <Badge className="counter" value={`+${capabilities.length - MAX_TAGS_ITEM}`} />
              </Tooltip>
            ) : null}
          </div>
        )}
        {targets && (
          <div className="template-card-component__information__targets">
            <span className="template-card-component__information__title">Nav. targets</span>
            {targets.slice(0, MAX_TAGS_ITEM).map((target, i) => (
              <TagBadge className="target-badge" key={`${target}-${i}`} value={target} />
            ))}
            {targets.length > MAX_TAGS_ITEM ? (
              <Tooltip
                align={{ points: ['br', 'tr'] }}
                overlay={
                  <div className="template-tooltips-content">
                    {targets.slice(MAX_TAGS_ITEM).map((target, i) => (
                      <TagBadge className="target-badge badge" key={`${target}-${i}`} value={target} />
                    ))}
                  </div>
                }
                placement="top"
                trigger="hover"
              >
                <Badge className="counter" value={`+${targets.length - MAX_TAGS_ITEM}`} />
              </Tooltip>
            ) : null}
          </div>
        )}
        <div className="template-card-component__information__version">
          <span className="template-card-component__information__title">Version:</span>
          {version}
        </div>
      </div>
    </div>
  )
}

export default TemplateCard
